import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from '@reach/router';
import axios from 'axios';
import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import { all, equals, or } from 'ramda';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAsync, useDeepCompareEffect } from 'react-use';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';
import TextButton from '@ui/atoms/TextButton';
import Toast from '@ui/atoms/Toast';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import MessageIconCard from '@ui/molecules/MessageIconCard';
import ReadMore from '@ui/molecules/ReadMore';
import SubmitButton from '@ui/molecules/SubmitButton';

import PastCancelDateAppointmentError from '@ui/routes/Dashboard/Appointments/RescheduleAppointment/PastCancelDateAppointmentError.component';
import GahtFuAppointmentBooking from '@ui/routes/GahtFu/GahtFuAppointmentSelectionStep/GahtFuAppointmentBooking.component';
import { mentalHealthTopicConstants } from '@ui/routes/MemberMH/MemberMHTopicSelectionStep';

import padded from '@ui/styled/Padded';
import spaced from '@ui/styled/Spaced';

import useMemberInfo, { SubscriptionInfo } from '@hooks/useMemberInfo';
import { useMemberIsVisitWaivable } from '@hooks/useMemberIsVisitWaivable';

import { showAppointmentToast } from '@helpers/Appointments/AppointmentHelpers';
import { getServiceType } from '@helpers/MentalHealth/mentalHealth';

import { interpolate } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { exists } from '@utils/general';

import { getAuthToken } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import Clock from '@assets/vectors/clock.inline.svg';

import { membership, membershipName } from '@graphql/fragments/membership';
import COMPLETE_FLOW from '@graphql/mutations/completeFlowMutation.gql';

export interface IGahtFuAppointmentSelectionStepPageContent {
	appointmentSelectionHeader: string;
	appointmentSelectionDescription: string;
	appointmentHeader: string;
	appointmentSubheader: string;
	appointmentDescription: string;
	cancellationHeader: string;
	cancellationDescription: string;
	editLabel: string;
	interpreterInfo: string;
	pastMembershipCancelDate: {
		cta: string;
		description: string;
		header: string;
		secondaryCta: string;
		subheader: string;
	};
	errorMessages: {
		stateServiceNotFound: string;
		noPriorGahtVisit: string;
		noProviderFound: string;
		hasUpcomingAppointment: string;
		genericMessage: string;
	};
}

interface IGahtFuAppointmentSelectionStepProps extends IFlowStepComponentProps {
	pageContent: IGahtFuAppointmentSelectionStepPageContent;
}

interface IScheduledAppointment {
	id: string;
	time: DateTime;
	justScheduled: boolean;
}

const SpacedGridItem = spaced(CenteredGridItem);
const SpacedColumn = spaced<IFlexColumn>(FlexColumn);
const SpacedHeader = spaced(HeadingText);
const PaddedSpacedWYSIWYG = padded(spaced(WYSIWYGContent));

const AppointmentSelectionStep: FC<IGahtFuAppointmentSelectionStepProps> = ({
	pageContent,
	stepKey,
	destinationFlow,
}) => {
	const {
		existingFormData,
		handleFormSubmission,
		handleNavBack,
		flowId,
		isPaymentWaived,
		setIsPaymentWaived = () => {
			return;
		},
	} = useContext(FlowContext);

	const location = useLocation();
	const { loading: memberLoading, memberInfo } = useMemberInfo<SubscriptionInfo>([
		{ name: membershipName, node: membership },
	]);

	const [markFlowComplete, { error: markCompleteError }] = useMutation(COMPLETE_FLOW);

	const serviceTypeForWaiveCheck =
		destinationFlow === 'mh'
			? getServiceType(existingFormData?.[mentalHealthTopicConstants.TOPIC_ID], { uppercase: true })
			: destinationFlow?.toUpperCase();
	const {
		data: isVisitWaivableData,
		loading: isVisitWaivableLoading,
		waiveVisit,
	} = useMemberIsVisitWaivable(serviceTypeForWaiveCheck);

	const schema = z.object({});
	type Schema = Partial<z.input<typeof schema>>;

	const [submitting, setSubmitting] = useState(false);
	const [shouldResetAppointment, setShouldResetAppointment] = useState(false);

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const { handleSubmit } = form;

	const resetAppointmentStatus = () => {
		setShouldResetAppointment(true);
	};

	const scheduledAppointment: IScheduledAppointment | undefined = useMemo(() => {
		if (['cogsworthId', 'cogsworthAppointmentTime'].every((key) => key in existingFormData)) {
			return {
				time: DateTime.fromISO(existingFormData?.cogsworthAppointmentTime as string),
				id: existingFormData?.cogsworthId as string,
				justScheduled: false,
			};
		}
	}, [existingFormData?.cogsworthId]);

	const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);
	const [hasBookingUrlErrors, setHasBookingUrlErros] = useState(false);

	const onBackClick = useCallback(
		() => (hasBookingUrlErrors ? navigate('/app/member/get_care/') : handleNavBack(stepKey)),
		[handleNavBack, hasBookingUrlErrors, stepKey]
	);

	const { value: isValidAppointment, loading: validAppointmentLoading } = useAsync(async () => {
		console.log('checking appointment validity');
		if (scheduledAppointment) {
			console.log('Found scheduled appointment');
			const authorizationToken = await getAuthToken();
			const res = await axios
				.get(`${ApiEndpoints.cogsworthValidateAppointment}/${scheduledAppointment?.id}`, {
					headers: {
						authorizationToken,
					},
				})
				.catch(() => {
					return false;
				});

			return res?.data?.isValid;
		}
	}, [scheduledAppointment]);

	useDeepCompareEffect(() => {
		setNextButtonDisabled(
			scheduledAppointment?.id === undefined ||
				isVisitWaivableLoading ||
				validAppointmentLoading ||
				!isValidAppointment
		);
	}, [scheduledAppointment, isVisitWaivableLoading, validAppointmentLoading, isValidAppointment]);

	useEffect(() => {
		showAppointmentToast(memberInfo?.stateOfResidence?.abbreviation);
	}, [memberInfo?.stateOfResidence?.abbreviation]);

	const appointmentDate = scheduledAppointment?.time;

	const localizedDate = appointmentDate?.toFormat('EEEE, MMMM d');
	const localizedTime = appointmentDate?.toFormat('h:mm a ZZZZ');

	const appointmentType = 'Follow-up';

	const AppointmentIframe = () => (
		<SpacedGridItem marginBottom="xl">
			<GahtFuAppointmentBooking
				loading={validAppointmentLoading || isVisitWaivableLoading || memberLoading}
				onBookingUrlError={() => setHasBookingUrlErros(true)}
				errorMessages={pageContent.errorMessages}
			/>
		</SpacedGridItem>
	);

	const scheduleAppointmentView = (
		<>
			<SpacedGridItem marginBottom="lg">
				<SpacedHeader marginBottom="sm">{pageContent.appointmentSelectionHeader}</SpacedHeader>
				<WYSIWYGContent
					dangerouslySetInnerHTML={{
						__html: pageContent.appointmentSelectionDescription,
					}}
				/>
			</SpacedGridItem>
			<AppointmentIframe />
		</>
	);

	const appointmentScheduledView = (
		<SpacedGridItem marginBottom="lgxl">
			<FlexColumn gap="sm">
				<HeadingText fontSize="sm">{pageContent.appointmentHeader}</HeadingText>
				<MessageIconCard
					bgColor="cardRed"
					message={pageContent.appointmentDescription}
					Icon={Clock}
					iconAlt="clock"
				/>
				<SpacedColumn gap="xssm" marginBottom="sm">
					<BasicText variant="small">
						{interpolate(pageContent.appointmentSubheader, { appointmentType })}
					</BasicText>
					<BasicText variant="body">{`${localizedDate} at ${localizedTime}`}</BasicText>
					<TextButton
						label={pageContent.editLabel}
						variant="text"
						underline
						onClick={() => resetAppointmentStatus()}
					/>
				</SpacedColumn>
				<ReadMore label={pageContent.cancellationHeader}>
					<WYSIWYGContent
						dangerouslySetInnerHTML={{ __html: pageContent.cancellationDescription }}
					/>
				</ReadMore>
				<PaddedSpacedWYSIWYG
					dangerouslySetInnerHTML={{ __html: pageContent.interpreterInfo }}
					textVariant="fineprint"
					marginBottom="lgxl"
					paddingLeft="md"
					paddingRight="md"
				/>
			</FlexColumn>
		</SpacedGridItem>
	);

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		if (!isPaymentWaived) {
			if (isVisitWaivableData?.isWaivable && scheduledAppointment?.id && serviceTypeForWaiveCheck) {
				const waiveVisitResponse = await waiveVisit(
					scheduledAppointment?.id,
					serviceTypeForWaiveCheck,
					existingFormData.formUuid
				);
				setIsPaymentWaived(waiveVisitResponse.waived);
				if (waiveVisitResponse.waived) {
					await markFlowComplete({
						variables: {
							flowName: flowId,
						},
					});
					if (markCompleteError) {
						console.error(
							`Error marking MH flow complete: ${JSON.stringify(markCompleteError.message)}`
						);
					}
				}
			}
		}
		await handleFormSubmission(
			exists(scheduledAppointment?.id)
				? {
						cogsworthId: scheduledAppointment?.id,
						cogsworthAppointmentTime: scheduledAppointment?.time,
						justScheduled: true,
						isVisitWaivable: isVisitWaivableData?.isWaivable,
						...data,
				  }
				: { ...data },
			stepKey
		);
		setSubmitting(false);
	};

	if (
		!shouldResetAppointment &&
		or(
			location.state?.isPastCancelledDate,
			all(equals(true), [
				exists(memberInfo?.membershipSubscription?.cancelAt),
				exists(appointmentDate),
				appointmentDate > DateTime.fromISO(memberInfo?.membershipSubscription?.cancelAt ?? ''),
			])
		)
	) {
		return (
			<CenteredGridItem>
				<PastCancelDateAppointmentError
					appointmentType={appointmentType}
					convertedDate={appointmentDate}
					loading={false}
					onClick={() => resetAppointmentStatus()}
					{...pageContent.pastMembershipCancelDate}
				/>
			</CenteredGridItem>
		);
	}

	return (
		<CenteredGridItem>
			<form onSubmit={handleSubmit(onSubmit)}>
				{scheduledAppointment &&
				exists(scheduledAppointment.id) &&
				isValidAppointment &&
				!shouldResetAppointment
					? appointmentScheduledView
					: scheduleAppointmentView}
				<SpacedGridItem marginBottom="xxxl">
					<NavigationSection>
						<Button label="Back" onClick={onBackClick} />
						<SubmitButton
							variant="secondary"
							label="Next"
							type="submit"
							submitting={submitting}
							labelSize="md"
							disabled={nextButtonDisabled}
						/>
					</NavigationSection>
				</SpacedGridItem>
			</form>
			<Toast
				position="top-center"
				theme="colored"
				pauseOnHover
				pauseOnFocusLoss={false}
				autoClose={7500}
			/>
		</CenteredGridItem>
	);
};

export default AppointmentSelectionStep;
